import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import GracePeriodBanner from './GracePeriodBanner';
import SuspendedPage from './SuspendedPage';
import ChurnedPage from './ChurnedPage';

interface SubscriptionStateGateProps {
  subscriptionState: string | undefined;
  subscriptionData: Record<string, any> | null;
  children: ReactNode;
}

/**
 * Gates the app based on subscription state.
 *
 * - active / trialing / missing: render children normally
 * - onboarding / pending_setup: redirect to /activation (except /upload and
 *   the Connected Accounts surface, so a mailbox can be linked and history
 *   ingested BEFORE the founder activates the tenant; dashboards stay locked)
 * - grace_period: render children with warning banner
 * - suspended: full-page block
 * - churned: full-page block with reactivation CTA
 *
 * White-glove companies (no subscription doc) have undefined state — allowed through.
 */
const SubscriptionStateGate: React.FC<SubscriptionStateGateProps> = ({
  subscriptionState,
  subscriptionData,
  children,
}) => {
  const location = useLocation();

  // No subscription data (white-glove) or active/trialing — allow through
  if (!subscriptionState || subscriptionState === 'active' || subscriptionState === 'trialing') {
    return <>{children}</>;
  }

  // Onboarding / pending_setup — redirect to activation, except:
  //   - /upload (historical exemption: lets statements be staged pre-activation)
  //   - the Connected Accounts surface (2026-08-31, mailbox-first onboarding):
  //     /settings/connected-accounts is the redirect shim + OAuth return target,
  //     and /settings?tab=connected-accounts is the rendered tab. Only that tab
  //     is exempted; navigating to any other tab changes the query string and
  //     re-triggers this gate. Everything else (dashboards included) stays
  //     locked until the founder approves activation.
  if (subscriptionState === 'onboarding' || subscriptionState === 'pending_setup') {
    const params = new URLSearchParams(location.search);
    const isConnectedAccounts =
      location.pathname === '/settings/connected-accounts' ||
      location.pathname === '/settings/connected-accounts/error' ||
      (location.pathname === '/settings' && params.get('tab') === 'connected-accounts');
    if (location.pathname !== '/upload' && !isConnectedAccounts) {
      return <Navigate to="/activation" replace />;
    }
    return <>{children}</>;
  }

  // Grace period — allow access but show warning banner
  if (subscriptionState === 'grace_period') {
    return (
      <>
        <GracePeriodBanner gracePeriodEndsAt={subscriptionData?.gracePeriodEndsAt} />
        {children}
      </>
    );
  }

  // Suspended — full block
  if (subscriptionState === 'suspended') {
    return <SuspendedPage />;
  }

  // Churned — full block with reactivation
  if (subscriptionState === 'churned') {
    return <ChurnedPage />;
  }

  // Unknown state — allow through (fail-open, same as backend)
  return <>{children}</>;
};

export default SubscriptionStateGate;
